import React from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import usePageMeta from "@/lib/usePageMeta";

const TAGLINE_WORDS = [
  "Rewritten.",
  "Redesigned.",
  "ATS-ready.",
];

export default function LoginPage({ mode = "login" }) {
  const [tab, setTab] = React.useState(mode); // "login" | "register"
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [pointer, setPointer] = React.useState({ x: 0.5, y: 0.5 });

  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const next = params.get("next") || "/account";

  usePageMeta({
    title:
      tab === "login"
        ? "Sign in | CV AI PRO"
        : "Create your account | CV AI PRO",
    description:
      tab === "login"
        ? "Sign in to your CV AI PRO account to manage your orders and delivered resumes."
        : "Create a CV AI PRO account to save your submissions and track your orders.",
    path: tab === "login" ? "/login" : "/signup",
  });

  React.useEffect(() => {
    if (user && user !== false) {
      navigate(next, { replace: true });
    }
  }, [user, navigate, next]);

  // Update tab when route path changes
  React.useEffect(() => {
    if (location.pathname === "/signup") setTab("register");
    else if (location.pathname === "/login") setTab("login");
  }, [location.pathname]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Missing information", {
        description: "Please enter your email and password.",
      });
      return;
    }
    if (tab === "register" && password.length < 8) {
      toast.error("Password too short", {
        description: "Use at least 8 characters.",
      });
      return;
    }
    setSubmitting(true);
    try {
      if (tab === "login") {
        await login(email.trim(), password);
        toast.success("Welcome back.", { description: email.trim() });
      } else {
        await register(email.trim(), password, name.trim() || undefined);
        toast.success("Account created.", { description: email.trim() });
      }
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(
        tab === "login" ? "Sign in failed" : "Registration failed",
        {
          description:
            formatApiErrorDetail(err?.response?.data?.detail) ||
            err.message ||
            "Please try again.",
        }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPointer({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  return (
    <div
      className="relative min-h-screen bg-black text-white overflow-hidden"
      data-testid="auth-page"
    >
      {/* Global grid + noise */}
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg opacity-50" />
      <div aria-hidden className="pointer-events-none absolute inset-0 noise" />

      {/* Top nav */}
      <header className="relative z-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="auth-logo"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="auth-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left — editorial brand panel */}
        <section
          onMouseMove={onMouseMove}
          className="relative hidden lg:flex flex-col justify-between rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black overflow-hidden min-h-[560px] p-10"
          data-testid="auth-hero-panel"
        >
          {/* Parallax radial glow tracking cursor */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            animate={{
              background: `radial-gradient(600px circle at ${pointer.x * 100}% ${pointer.y * 100}%, rgba(99,102,241,0.18), transparent 60%)`,
            }}
            transition={{ type: "spring", damping: 20, stiffness: 80 }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-600/20 blur-[100px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-purple-600/20 blur-[100px]"
          />

          {/* Kinetic word-mask reveal */}
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.22em] text-zinc-500">
              CV AI PRO / 2026
            </p>
            <div className="mt-8 space-y-2">
              {TAGLINE_WORDS.map((w, i) => (
                <div key={w} className="overflow-hidden">
                  <motion.h2
                    initial={{ y: "110%", opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.15 + i * 0.12,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="text-6xl xl:text-7xl font-semibold tracking-tighter text-white leading-[1]"
                  >
                    {w}
                  </motion.h2>
                </div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.6 }}
              className="mt-10 max-w-sm text-sm text-zinc-400 leading-relaxed"
            >
              Sign in to manage your CV AI PRO orders, track deliveries, and
              access every resume we&apos;ve crafted for you.
            </motion.p>
          </div>

          {/* Slow editorial marquee */}
          <div className="relative mt-16">
            <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black to-transparent z-10" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black to-transparent z-10" />
            <div className="overflow-hidden">
              <motion.div
                className="flex gap-10 whitespace-nowrap text-zinc-500 text-xs uppercase tracking-[0.24em]"
                animate={{ x: ["0%", "-50%"] }}
                transition={{
                  duration: 45,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                {Array.from({ length: 2 }).flatMap(() => [
                  "Done-for-you resumes",
                  "·",
                  "ATS-optimized",
                  "·",
                  "Delivered in minutes",
                  "·",
                  "Human-reviewed",
                  "·",
                  "One-time payment",
                  "·",
                ]).map((w, i) => (
                  <span key={i} className="shrink-0">
                    {w}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Right — form */}
        <section
          className="relative rounded-3xl border border-white/10 bg-zinc-950/60 p-8 sm:p-10 backdrop-blur-sm"
          data-testid="auth-form-panel"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-indigo-600/15 blur-[100px]"
          />

          <div className="relative flex items-center gap-1 rounded-full bg-white/5 border border-white/10 p-1 w-fit">
            {[
              { id: "login", label: "Sign in", to: "/login" },
              { id: "register", label: "Create account", to: "/signup" },
            ].map((t) => (
              <Link
                key={t.id}
                to={t.to}
                onClick={() => setTab(t.id)}
                className={`relative px-4 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  tab === t.id ? "text-black" : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`auth-tab-${t.id}`}
              >
                {tab === t.id && (
                  <motion.span
                    layoutId="auth-tab-bg"
                    className="absolute inset-0 bg-white rounded-full"
                    transition={{ type: "spring", damping: 24, stiffness: 260 }}
                  />
                )}
                <span className="relative">{t.label}</span>
              </Link>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="mt-8"
            >
              <h1
                className="text-3xl sm:text-4xl font-semibold tracking-tighter text-white"
                data-testid="auth-headline"
              >
                {tab === "login" ? "Welcome back." : "Create your account."}
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                {tab === "login"
                  ? "Sign in to your CV AI PRO account."
                  : "It only takes a moment. No credit card required."}
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="auth-form">
                {tab === "register" && (
                  <Field
                    id="auth-name"
                    label="Full name"
                    icon={UserIcon}
                    placeholder="Jane Doe"
                    value={name}
                    onChange={setName}
                    testid="auth-input-name"
                    autoComplete="name"
                  />
                )}
                <Field
                  id="auth-email"
                  label="Email"
                  icon={Mail}
                  type="email"
                  required
                  placeholder="you@email.com"
                  value={email}
                  onChange={setEmail}
                  testid="auth-input-email"
                  autoComplete="email"
                />
                <Field
                  id="auth-password"
                  label="Password"
                  icon={Lock}
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder={tab === "register" ? "At least 8 characters" : "••••••••"}
                  value={password}
                  onChange={setPassword}
                  testid="auth-input-password"
                  autoComplete={tab === "login" ? "current-password" : "new-password"}
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-zinc-500 hover:text-white transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      data-testid="auth-toggle-password"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />

                {tab === "login" && (
                  <div className="flex justify-end -mt-1">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-zinc-500 hover:text-white transition-colors"
                      data-testid="auth-forgot-password"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                  data-testid="auth-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {tab === "login" ? "Signing in…" : "Creating account…"}
                    </>
                  ) : (
                    <>
                      {tab === "login" ? "Sign in" : "Create account"}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>

                <p className="text-center text-xs text-zinc-500">
                  {tab === "login" ? (
                    <>
                      New here?{" "}
                      <Link
                        to="/signup"
                        className="text-zinc-300 hover:text-white underline underline-offset-2"
                        data-testid="auth-switch-signup"
                      >
                        Create an account
                      </Link>
                    </>
                  ) : (
                    <>
                      Already have an account?{" "}
                      <Link
                        to="/login"
                        className="text-zinc-300 hover:text-white underline underline-offset-2"
                        data-testid="auth-switch-login"
                      >
                        Sign in
                      </Link>
                    </>
                  )}
                </p>
              </form>
            </motion.div>
          </AnimatePresence>

          <p className="mt-8 text-[11px] text-zinc-500 text-center">
            By continuing you agree to our{" "}
            <Link to="/terms" className="text-zinc-400 hover:text-white underline underline-offset-2">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-zinc-400 hover:text-white underline underline-offset-2">
              Privacy Policy
            </Link>
            .
          </p>
        </section>
      </main>
    </div>
  );
}

const Field = ({
  id,
  label,
  icon: Icon,
  type = "text",
  placeholder,
  value,
  onChange,
  testid,
  required,
  suffix,
  autoComplete,
}) => {
  const [focused, setFocused] = React.useState(false);
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-zinc-400 mb-1.5">
        {label}
      </label>
      <div
        className={`group relative flex items-center rounded-xl border transition-colors bg-black/40 ${
          focused
            ? "border-white/30 ring-1 ring-white/10"
            : "border-white/10 hover:border-white/20"
        }`}
      >
        <Icon className="ml-3 h-4 w-4 text-zinc-500 shrink-0" />
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          required={required}
          autoComplete={autoComplete}
          data-testid={testid}
          className="flex-1 bg-transparent px-3 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none"
        />
        {suffix && <div className="pr-3">{suffix}</div>}
      </div>
    </div>
  );
};
